import { makeVar, useReactiveVar } from '@apollo/client';
import {
  Body,
  Box,
  Checkbox,
  Flex,
  Heading,
  Sheet,
  useToast,
} from '@hover/blueprint';
import React, { useCallback, useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';

import { CreatingProjectLoading } from 'src/modules/homeownerApp/components/UploadImage/CreatingProjectLoading';
import { Button, LinkExternal } from 'src/shared/components';
import { Web } from 'src/shared/constants/FeatureFlag';
import { designerAnonymousSessionVar } from 'src/shared/graphql/client/link/designerAnonymousSession';
import type { AiOriginalImage } from 'src/shared/graphql/generated';
import { useAiSpaceTypesQuery } from 'src/shared/graphql/generated';
import {
  TranslationNamespace,
  useIsEmbed,
  useTimeout,
  useTranslate,
  useTreatment,
} from 'src/shared/hooks';
import { useGlobalStorageContext } from 'src/shared/hooks/useGlobalStorage';
import { External, Route } from 'src/shared/route';
import { EventType, emitPageAction } from 'src/shared/telemetry';
import { isEnabled } from 'src/shared/telemetry/featureFlag';

import homeownerAppTranslations from '../../../../../locales/en-US/homeownerApp.json'; // eslint-disable-line
import {
  useActiveOriginalImage,
  useHomeownerAppContext,
  useViewportSize,
} from '../../hooks';
import { useUploadOriginalImage } from '../../hooks/useUploadOriginalImage';
import { HomeownerAppMenuNames } from '../../providers/HomeownerAppProvider';
import { OriginalImageStatus } from '../../static_values';
import { isSample } from '../../types';

import { SpaceTypeSelector } from './SpaceTypeSelector';
import { UploadImageBox } from './UploadImageBox';

const VERTICAL_DRAWER_PADDING_PIXELS = 60;
const UPLOAD_IMAGE_STATUS_POLLING_INTERVAL = 1000;
const UPLOAD_IMAGE_STATUS_ERROR_TIMEOUT = 30000;

export const isUploadingOriginalImageVar = makeVar<boolean>(false);

const UploadImage = ({
  email,
  handleImageStatusDone,
  orgId,
  orgDisplayName,
  setImageFileToUpload,
  imageFileToUpload,
  fromLLP = false,
}: {
  email?: string;
  orgId?: number;
  orgDisplayName?: string;
  handleImageStatusDone: ({ image }: { image: AiOriginalImage }) => void;
  imageFileToUpload: File | undefined;
  setImageFileToUpload: (imageFile?: File | undefined) => void;
  fromLLP?: boolean;
}) => {
  const isEmbed = useIsEmbed();
  const consentMandatory = isEmbed;

  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);

  const history = useHistory();
  const errorToast = useToast({
    duration: 5000,
    isClosable: true,
  });

  const { height: viewportHeight } = useViewportSize();
  const [uploadedImageId, setUploadedImageId] = useState<number | undefined>();
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [spaceType, setSpaceType] = useState<string>('');
  const [spaceTypes, setSpaceTypes] = useState<string[]>([]);
  const [imageUrl, setImageUrl] = useState<string>('');
  const [agreedToTerms, setAgreedToTerms] = useState<boolean>(false);
  const { loggedIn, trackingData, setCurrentMenu } = useHomeownerAppContext();
  const location = useLocation<{ preSelectedSpaceType?: string }>();
  const { setHomeownerAppSpaceType, setAffOrgPublicId } =
    useGlobalStorageContext();
  const searchParams = new URLSearchParams(location.search);
  const { preSelectedSpaceType } = location.state ?? {};
  const affOrgId = searchParams.get('aff_org_id');
  const isNewProjectUiEnabled = isEnabled(useTreatment(Web.NEW_PROJECT_UI));

  const setUploading = useCallback((uploading: boolean) => {
    setIsUploading(uploading);
    isUploadingOriginalImageVar(uploading);
  }, []);

  useEffect(() => {
    if (!affOrgId) return;

    setAffOrgPublicId(affOrgId);
  }, [affOrgId, setAffOrgPublicId]);

  const designerAnonymousSession = useReactiveVar(designerAnonymousSessionVar);

  const { image, startPolling, stopPolling } = useActiveOriginalImage({
    originalImageId: uploadedImageId,
    options: { fetchPolicy: 'network-only' },
  });

  const { data: spaceTypesData } = useAiSpaceTypesQuery({
    variables: { orgId },
    // Does not get shown to user on load
    ssr: false,
  });

  useEffect(() => {
    setSpaceTypes(spaceTypesData?.aiSpaceTypes?.spaceTypes ?? []);
  }, [spaceTypesData]);

  useEffect(() => {
    if (uploadedImageId && !image?.status) {
      startPolling(UPLOAD_IMAGE_STATUS_POLLING_INTERVAL);
    }

    return stopPolling;
  }, [startPolling, uploadedImageId, stopPolling, image?.status]);

  const uploadImage = useUploadOriginalImage();

  useEffect(() => {
    const url = imageFileToUpload ? URL.createObjectURL(imageFileToUpload) : '';
    setImageUrl(url);
    return url ? () => URL.revokeObjectURL(url) : () => null;
  }, [imageFileToUpload]);

  const resetUploadForm = useCallback(() => {
    setUploading(false);
    setImageFileToUpload();
    setSpaceType('');
    setAgreedToTerms(false);
    setUploadedImageId(undefined);
  }, [setImageFileToUpload, setUploading]);

  const handleClose = () => {
    history.replace({
      pathname: history.location.pathname,
      state: {
        ...(history.location.state ?? {}),
        preSelectedSpaceType: undefined,
      },
    });
    resetUploadForm();
    setAffOrgPublicId();
  };

  const [serverTimeout, clearServerTimeout] = useTimeout();
  const missingConsent = !agreedToTerms && consentMandatory;

  const handleSubmit = async (overrideSpaceType?: string) => {
    const spaceTypeToUse = overrideSpaceType ?? spaceType;
    setHomeownerAppSpaceType(spaceTypeToUse);

    // No action if no image is selected
    if (!imageFileToUpload) return;

    // check if user is logged in
    // all genai should be behind authentication:
    // cf. https://hoverinc.atlassian.net/wiki/spaces/EN/pages/3463479328/2024-04-17+Disallow+users+from+uploading+PII+unauthenticated
    // TODO: We are temporarily allowing anonymous sessions to experiment with
    //  third party integrations via an embedded experience. When the experiment
    //  is over, remove the allowance for designerAnonymousSession
    if (!loggedIn && !designerAnonymousSession && !fromLLP) {
      setCurrentMenu(HomeownerAppMenuNames.SIGN_IN);
      return;
    }

    setUploading(true);

    let marketingConsent;
    if (agreedToTerms) {
      marketingConsent = homeownerAppTranslations.uploadImage.embedLegalText;
    }

    try {
      const uploadedImage = await uploadImage({
        image: imageFileToUpload,
        email,
        spaceType: spaceTypeToUse,
        marketingConsent,
      });

      const newOriginalImageId = uploadedImage?.id;
      setUploadedImageId(newOriginalImageId);

      if (newOriginalImageId) {
        emitPageAction(EventType.DESIGNER_ORIGINAL_IMAGE_UPLOADED, {
          aiOriginalImageId: `${newOriginalImageId}`,
        });
      }
    } catch (e) {
      errorToast({
        status: 'error',
        description: t('uploadPhotoSheet.uploadErrorMessage'),
      });
      setUploading(false);
    }
  };

  useEffect(() => {
    const originalSpaceTypes = spaceTypesData?.aiSpaceTypes?.spaceTypes ?? [];
    const filteredSpaceTypes = preSelectedSpaceType
      ? originalSpaceTypes.filter(type => type !== 'exterior')
      : originalSpaceTypes;

    setSpaceTypes(filteredSpaceTypes);
  }, [preSelectedSpaceType, spaceTypesData?.aiSpaceTypes?.spaceTypes]);

  useEffect(() => {
    if (isUploading) {
      return;
    }

    if (
      imageFileToUpload &&
      preSelectedSpaceType === 'exterior' &&
      !missingConsent
    ) {
      // auto submit
      void handleSubmit(preSelectedSpaceType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUploading, imageFileToUpload, preSelectedSpaceType]);

  useEffect(() => {
    if (uploadedImageId && !image?.status) {
      serverTimeout(() => {
        if (image?.status) return;

        stopPolling();
        resetUploadForm();
        history.replace({
          pathname: Route.HOMEOWNER_APP_HOME.path,
          state: {
            ...(history.location.state ?? {}),
            uploadErrorMessage: 'No status',
          },
        });
      }, UPLOAD_IMAGE_STATUS_ERROR_TIMEOUT);
    }

    return clearServerTimeout;
  }, [
    uploadedImageId,
    serverTimeout,
    clearServerTimeout,
    history,
    image?.status,
    stopPolling,
    resetUploadForm,
  ]);

  useEffect(() => {
    // AiOriginalImage status check
    if (!uploadedImageId || !isUploading) return;
    if (isSample(image as AiOriginalImage)) return;
    if (!image) return;

    if (image?.status !== null) {
      stopPolling();
      resetUploadForm();
    }

    if (image?.status === OriginalImageStatus.FAILED) {
      history.replace({
        pathname: Route.HOMEOWNER_APP_HOME.path,
        state: {
          ...(history.location.state ?? {}),
          uploadErrorMessage: image.statusDescription,
        },
      });
    } else if (image?.status === OriginalImageStatus.DONE) {
      handleImageStatusDone({ image: image as AiOriginalImage });
    }
  }, [
    isUploading,
    history,
    image,
    image?.status,
    stopPolling,
    uploadedImageId,
    handleImageStatusDone,
    spaceType,
    orgDisplayName,
    orgId,
    resetUploadForm,
  ]);

  if (!imageFileToUpload) return null;
  if (preSelectedSpaceType && isUploading)
    return <CreatingProjectLoading imageUrl={imageUrl} />;

  return (
    <Sheet
      autoFocus={false}
      blockScrollOnMount={!isEmbed}
      isOpen={!!imageUrl}
      onClose={handleClose}
      variant="bottom"
    >
      <Box
        alignItems={{ base: 'center' }}
        backgroundColor="neutral.0"
        data-testid="upload-image-box-wrapper"
        flexDirection={{ base: 'column', desktop: 'row-reverse' }}
        height={{
          base: `calc(${viewportHeight} - ${VERTICAL_DRAWER_PADDING_PIXELS}px)`,
        }}
        overflow="auto"
        pb={{ desktop: 700 }}
        pt={{ base: 400, tablet: 1000, desktop: 'unset' }}
        px={{ desktop: 500 }}
      >
        <UploadImageBox
          borderRadius={400}
          height="100%"
          imageUrl={imageUrl}
          maxHeight={{ base: '240px', tablet: '375px', desktop: '575px' }}
          minHeight={{ base: 600, tablet: 700, desktop: 750 }}
        />
        <Box
          flex={1}
          flexDirection="column"
          justifyContent={{ desktop: 'center' }}
        >
          <Heading
            alignSelf={{
              base: 'flex-start',
              tablet: 'center',
              desktop: 'flex-start !important',
            }}
            fontWeight={700}
            m="0 !important"
            maxW={{ base: '280px', tablet: 750, desktop: '440px' }}
            pb={{ base: 500, tablet: 700, desktop: 600 }}
            pt={{ base: 600, tablet: 800, desktop: 'unset' }}
            size={{ base: 'large', tablet: 700 }}
            textAlign={{ base: 'left', tablet: 'center', desktop: 'left' }}
          >
            {t('uploadImage.whatTypeOfSpace')}
          </Heading>
          <SpaceTypeSelector
            setSpaceType={setSpaceType}
            spaceType={spaceType}
            spaceTypes={spaceTypes}
            width={{ tablet: 850, desktop: 800 }}
          />
          <Box
            alignItems={{
              base: 'flex-start',
              tablet: 'center',
              desktop: 'flex-start',
            }}
            data-testid="upload-submit-wrapper"
            flexDirection="column"
            gap={{ base: 400, tablet: 600, desktop: 600 }}
            height={{ base: '100%', tablet: 'unset' }}
            justifyContent={{ base: 'flex-end', desktop: 'flex-start' }}
            minWidth="100%"
            pb={{ base: 400, tablet: 900, desktop: 'unset' }}
            pt={{ base: 400, tablet: 600, desktop: 600 }}
          >
            <Flex flex={1} flexDirection="column" justifyContent="flex-end">
              {consentMandatory && (
                <Checkbox
                  alignItems="flex-start"
                  checked={agreedToTerms}
                  marginLeft={100}
                  onChange={e => {
                    setAgreedToTerms(e.target.checked);
                  }}
                >
                  <Body
                    color="textLight"
                    marginBottom={0}
                    marginTop={0}
                    paddingTop={0}
                    size={{ base: 300, tablet: 400 }}
                  >
                    <Trans
                      components={{
                        SupportEmailLink: (
                          <LinkExternal
                            color="inherit"
                            href={External.SUPPORT_EMAIL.path}
                          />
                        ),
                      }}
                      i18nKey="uploadImage.embedLegalText"
                      t={t}
                    />
                  </Body>
                </Checkbox>
              )}
            </Flex>
            <Button
              data-testid="upload-image-submit-button"
              height={500}
              isDisabled={!spaceType || missingConsent}
              isLoading={isUploading}
              mb={{ base: 50, tablet: 'unset', desktop: 'unset' }}
              mt={{ tablet: 200, desktop: 'unset' }}
              onClick={() => handleSubmit()}
              optionalTrackingProperties={{
                ...trackingData,
                button_location: 'bottom-main-page',
                track_id: 'tos-generate-images',
                primary_cta: true,
              }}
              width={{ base: '100%', tablet: '195px' }}
            >
              {isNewProjectUiEnabled
                ? t('uploadImage.createProject')
                : t('uploadImage.createDesigns')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Sheet>
  );
};

export { UploadImage };
